import {
  createProviderMethodService,
  deleteProviderMethodService,
  getProviderMethodByIdService,
  getProviderMethodsService,
  toggleProviderMethodActiveService,
  updateProviderMethodService,
} from "@/services/provider-methods.service";
import type {CreateProviderMethodDto, UpdateProviderMethodDto} from "@/types/provider-method.types";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";

const PROVIDER_METHODS_KEY = ["provider-methods"] as const;

interface ProviderMethodFilters {
  country?: string;
  provider?: string;
  operation?: string;
  active?: string;
  limit?: number;
  offset?: number;
}

export const useProviderMethods = (filters: ProviderMethodFilters = {}) =>
  useQuery({
    queryKey: [...PROVIDER_METHODS_KEY, filters],
    queryFn: async () => {
      const {data} = await getProviderMethodsService(filters);
      if (data === undefined) throw new Error("No data");
      return data;
    },
  });

export const useProviderMethod = (id: string) =>
  useQuery({
    queryKey: [...PROVIDER_METHODS_KEY, id],
    queryFn: async () => {
      const {data} = await getProviderMethodByIdService(id);
      if (data === undefined) throw new Error("No data");
      return data;
    },
    enabled: !!id,
  });

export const useCreateProviderMethod = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProviderMethodDto) => createProviderMethodService(body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: PROVIDER_METHODS_KEY});
    },
  });
};

export const useUpdateProviderMethod = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({id, body}: {id: string; body: UpdateProviderMethodDto}) => updateProviderMethodService(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: PROVIDER_METHODS_KEY});
    },
  });
};

export const useToggleProviderMethodActive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({id, isActive}: {id: string; isActive: boolean}) => toggleProviderMethodActiveService(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: PROVIDER_METHODS_KEY});
    },
  });
};

export const useDeleteProviderMethod = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteProviderMethodService(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: PROVIDER_METHODS_KEY});
    },
  });
};
